import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import { AppShell } from "@/components/bi/AppShell";
import { EmptyState } from "@/components/bi/EmptyState";
import { Loading } from "@/components/bi/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { buildOption, formatNumber, PALETTE } from "@/lib/chartTheme";
import { Compass, Calendar, Hash, Type as TypeIcon, ToggleLeft, Sparkles, Loader2, SendHorizonal, RefreshCw, Sigma, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const typeIcon = { datetime: Calendar, number: Hash, category: TypeIcon, boolean: ToggleLeft };

export default function DatasetExplorer() {
  const { id } = useParams();
  const nav = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [current, setCurrent] = useState(id || "");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/datasets").then(({ data }) => setDatasets(data)).catch(() => {}); }, []);
  useEffect(() => { if (id) setCurrent(id); }, [id]);
  useEffect(() => {
    if (!current) return;
    setLoading(true); setPreview(null);
    api.get(`/datasets/${current}/preview`, { params: { limit: 100 } })
      .then(({ data }) => setPreview(data)).catch(() => toast.error("Failed to load preview")).finally(() => setLoading(false));
  }, [current]);

  const ds = datasets.find((d) => d.id === current);
  const [refreshing, setRefreshing] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);

  const reloadPreview = () => {
    if (!current) return;
    setLoading(true); setPreview(null);
    api.get(`/datasets/${current}/preview`, { params: { limit: 100 } })
      .then(({ data }) => setPreview(data)).catch(() => toast.error("Failed to load preview")).finally(() => setLoading(false));
  };

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/datasets/${current}/refresh`);
      toast.success("Live data refreshed");
      reloadPreview();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const picker = (
    <div className="flex items-center gap-2">
      {ds && (
        <Button data-testid="calc-fields-btn" variant="outline" className="bg-[color:var(--surface)]" onClick={() => setCalcOpen(true)} title="Calculated fields">
          <Sigma className="w-4 h-4 mr-1" />Calculated fields
        </Button>
      )}
      {ds?.mode === "live" && (
        <Button data-testid="explore-refresh-btn" variant="outline" className="bg-[color:var(--surface)]" onClick={doRefresh} disabled={refreshing}>
          <RefreshCw className={"w-4 h-4 mr-1 " + (refreshing ? "animate-spin" : "")} />Refresh
        </Button>
      )}
      <Select value={current} onValueChange={(v) => { setCurrent(v); nav(`/explore/${v}`); }}>
        <SelectTrigger data-testid="explore-dataset-select" className="w-[220px] bg-[color:var(--surface)]"><SelectValue placeholder="Select dataset" /></SelectTrigger>
        <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <AppShell title="Explore" actions={picker}>
      {ds && <CalcFieldsDialog open={calcOpen} onOpenChange={setCalcOpen} ds={ds} onSaved={() => { api.get("/datasets").then(({ data }) => setDatasets(data)).catch(() => {}); reloadPreview(); }} />}
      <div className="px-5 md:px-8 py-6">
        {datasets.length === 0 ? (
          <EmptyState icon={Compass} title="No datasets to explore" description="Add a dataset first from Data Sources." action={<Button onClick={() => nav("/data")} className="bg-[color:var(--brand-ocean)] text-white">Go to Data Sources</Button>} />
        ) : !current ? (
          <EmptyState icon={Compass} title="Pick a dataset" description="Choose a dataset above to preview its rows, inspect the schema, and ask AI questions." />
        ) : loading ? <Loading label="Loading dataset" /> : preview && (
          <div className="space-y-6">
            <AiQuickAsk datasetId={current} />
            <Tabs defaultValue="preview">
              <TabsList className="bg-[color:var(--surface)]">
                <TabsTrigger value="preview" data-testid="tab-preview">Preview · {preview.row_count?.toLocaleString()} rows</TabsTrigger>
                <TabsTrigger value="schema" data-testid="tab-schema">Schema · {preview.schema?.length} cols</TabsTrigger>
              </TabsList>
              <TabsContent value="preview">
                <div data-testid="dataset-preview-scroll" className="glass-card overflow-auto max-h-[560px] overscroll-contain" style={{ scrollbarWidth: "thin" }}>
                  <table className="w-max min-w-full text-sm">
                    <thead className="sticky top-0 bg-[color:var(--surface-2)]"><tr>{preview.columns.map((c) => <th key={c} className="text-left px-3 py-2.5 font-medium text-[color:var(--ink-2)] whitespace-nowrap">{c}</th>)}</tr></thead>
                    <tbody>{preview.rows.map((r, i) => <tr key={i} className="border-t border-[color:var(--glass-border)] hover:bg-[color:var(--glass-bg)]">{preview.columns.map((c) => <td key={c} className="px-3 py-1.5 text-[color:var(--ink-2)] whitespace-nowrap">{String(r[c] ?? "—")}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              </TabsContent>
              <TabsContent value="schema">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {preview.schema.map((c) => {
                    const Icon = typeIcon[c.type] || TypeIcon;
                    return (
                      <div key={c.name} className="glass-card p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-[color:var(--surface-2)] flex items-center justify-center"><Icon className="w-4 h-4 text-[color:var(--brand-ocean)]" /></div>
                          <div className="min-w-0"><div className="font-medium text-[color:var(--ink)] truncate">{c.name}</div><div className="text-xs text-[color:var(--ink-3)]">{c.type} · {c.role}</div></div>
                        </div>
                        <div className="text-xs text-[color:var(--ink-3)] mt-3">{c.unique_count} unique · e.g. {(c.sample_values || []).slice(0, 2).join(", ")}</div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function AiQuickAsk({ datasetId }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const ask = async () => {
    if (!q.trim()) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/ask", { dataset_id: datasetId, question: q });
      setResult(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "AI failed"); }
    finally { setLoading(false); }
  };

  const option = result && ["kpi", "table"].indexOf(result.chart_spec.chart_type) === -1
    ? buildOption(result.chart_spec.chart_type, result.data, result.chart_spec, { colors: PALETTE }) : null;

  return (
    <div className="glass-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[color:rgba(255,107,53,0.14)] flex items-center justify-center"><Sparkles className="w-4 h-4 text-[color:var(--brand-mint)]" /></div>
        <h3 className="font-display text-base text-[color:var(--ink)]">Ask your data</h3>
      </div>
      <div className="flex gap-2">
        <Input data-testid="ai-quick-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="e.g. total sales by region, or top 5 products by revenue" className="bg-[color:var(--surface)]" />
        <Button data-testid="ai-quick-send" onClick={ask} disabled={loading} className="bg-[color:var(--brand-mint)] hover:bg-[#28a190] text-white">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizonal className="w-4 h-4" />}</Button>
      </div>
      {result && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 h-[300px] bg-[color:var(--glass-bg)] rounded-xl border border-[color:var(--glass-border)] p-2">
            {result.chart_spec.chart_type === "kpi"
              ? <div className="h-full flex flex-col items-center justify-center"><div className="font-display text-5xl text-[color:var(--ink)]">{formatNumber(Object.values(result.data.rows[0] || {})[0])}</div><div className="text-sm text-[color:var(--ink-3)] mt-2">{result.chart_spec.title}</div></div>
              : option ? <ReactECharts option={option} style={{ height: "100%", width: "100%" }} opts={{ renderer: "svg" }} notMerge />
              : <div className="h-full flex items-center justify-center text-[color:var(--ink-3)] text-sm">Result ready</div>}
          </div>
          <div className="glass-card p-4">
            <div className="text-xs font-medium text-[color:var(--brand-ocean)] uppercase tracking-wide">Insight</div>
            <p className="text-sm text-[color:var(--ink-2)] mt-2 leading-relaxed">{result.insight || "—"}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function CalcFieldsDialog({ open, onOpenChange, ds, onSaved }) {
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setFields((ds.calc_fields || []).map((f) => ({ ...f })));
    api.get(`/datasets/${ds.id}`).then(({ data }) => setFields((data.calc_fields || []).map((f) => ({ ...f })))).catch(() => {});
    // hydrate from the prop snapshot once, then fetch the fresh copy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ds.id]);
  const update = (i, k, v) => setFields(fields.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/datasets/${ds.id}/calc-fields`, { fields: fields.filter((f) => f.name && f.expr) });
      toast.success("Calculated fields saved");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sigma className="w-5 h-5 text-[color:var(--brand-ocean)]" />Calculated fields — {ds.name}</DialogTitle>
          <DialogDescription>Add derived columns with formulas using existing columns, e.g. <code>price * qty</code> or <code>`unit price` * quantity</code> (backticks for names with spaces). Available everywhere: charts, filters, alerts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1 max-h-[45vh] overflow-y-auto">
          {fields.map((f, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              <Input data-testid="calc-field-name" className="w-[160px]" placeholder="field_name" value={f.name} onChange={(e) => update(i, "name", e.target.value)} />
              <Input data-testid="calc-field-expr" className="flex-1 font-mono text-xs" placeholder="e.g. amount * 1.18" value={f.expr} onChange={(e) => update(i, "expr", e.target.value)} />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-[color:var(--ink-3)]" onClick={() => setFields(fields.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
          <Button data-testid="calc-field-add" size="sm" variant="ghost" className="text-[color:var(--brand-ocean)]" onClick={() => setFields([...fields, { name: "", expr: "" }])}><Plus className="w-3.5 h-3.5 mr-1" />Add field</Button>
          <div className="text-[11px] text-[color:var(--ink-3)]">Columns: {(ds.schema || []).slice(0, 12).map((c) => c.name).join(", ")}{(ds.schema || []).length > 12 ? "…" : ""}</div>
        </div>
        <DialogFooter>
          <Button data-testid="calc-fields-save" onClick={save} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Validate & save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
